import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, setAuthHeader, formatApiError, isCachedTokenValid } from '@/lib/api';
import { setProfile, getProfile } from '@/lib/offlineDb';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offlineMode, setOfflineMode] = useState(false);

  const refresh = async () => {
    // Skip the auth ping on purely public routes (Privacy, Terms, Install) so the
    // console stays clean for Google OAuth branding screenshots.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const PUBLIC_PATHS = ['/privacy', '/terms', '/install'];
    if (PUBLIC_PATHS.includes(path)) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      setOfflineMode(false);
      // Persist profile snapshot for next offline boot.
      try { await setProfile(data); } catch {}
    } catch (e) {
      // Network down OR no token. If we have a still-valid cached JWT + cached
      // profile, allow the user to continue offline. Otherwise treat as logged out.
      const isNetworkErr = !e?.response;
      if (isNetworkErr) {
        const valid = await isCachedTokenValid();
        const cached = await getProfile().catch(() => null);
        if (valid && cached) {
          setUser(cached);
          setOfflineMode(true);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.token) setAuthHeader(data.token);
      setUser(data.user);
      setOfflineMode(false);
      try { await setProfile(data.user); } catch {}
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  const register = async (payload) => {
    setError(null);
    try {
      const { data } = await api.post('/auth/register', payload);
      if (data.token) setAuthHeader(data.token);
      setUser(data.user);
      try { await setProfile(data.user); } catch {}
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setAuthHeader(null);
    setUser(null);
    setOfflineMode(false);
  };

  const loginWithGoogle = async (credential, role) => {
    setError(null);
    try {
      const { data } = await api.post('/auth/google', { credential, role });
      if (data.token) setAuthHeader(data.token);
      setUser(data.user);
      try { await setProfile(data.user); } catch {}
      return data.user;
    } catch (e) {
      setError(formatApiError(e));
      throw e;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, error, offlineMode, login, register, logout, refresh, loginWithGoogle }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
